import { z } from 'zod';
import { CalendarDate } from './project.js';
/**
 * AI xếp lại lịch khi có rủi ro — APM-1313.
 *
 * ## ⚠️ "Không xếp được" là một kết quả HỢP LỆ
 *
 * Cám dỗ ở đây là để model cố sinh ra một phương án cho bằng được. Nó sẽ sinh
 * ra một phương án **trông hợp lý** bằng cách âm thầm cho ai đó mười bốn tiếng
 * mỗi ngày — và một phương án như thế còn tệ hơn không có phương án nào, vì PM
 * duyệt nó rồi mới phát hiện ra khi đội không kịp.
 *
 * Nên schema này cho phép `moves` rỗng kèm `shortfall` nói **cần thêm gì**:
 * bao nhiêu người với kỹ năng nào trong bao nhiêu ngày, hoặc phải giãn hạn bao
 * nhiêu ngày. Bắt tối thiểu một `move` thì model buộc phải bịa — đúng thứ luật
 * này tồn tại để chặn.
 *
 * ## Vì sao mọi phương án phải chạy lại qua bộ kiểm năng lực
 *
 * `check_reschedule_plan` (tool) và `RescheduleValidator` (server) dùng **cùng
 * một** phép tính với `APM-1310`/`APM-1311`: giờ khả dụng thật của người đó, đã
 * trừ nghỉ phép, ngày lễ, ngày cuối làm việc và phần cam kết cho dự án. Một bộ
 * kiểm thứ hai viết riêng cho AI sẽ nới tay ở đúng chỗ khó, và không ai biết.
 */
export const RescheduleMove = z.object({
    /** `PRJ-12` — mã người đọc đối chiếu được. Model nêu mã, server tra ra id. */
    taskKey: z.string().min(1).max(40),
    /** `null` = giữ nguyên người phụ trách. */
    newAssigneeId: z.uuid().nullable(),
    /** Tên người nhận, để PM đọc diff mà không phải tra id. */
    newAssigneeName: z.string().max(200).nullable(),
    /** `null` = giữ nguyên ngày. Đổi ngày thì phải đủ **cả hai**. */
    newStartDate: CalendarDate.nullable(),
    newDueDate: CalendarDate.nullable(),
    /** Vì sao đổi. Một câu, đọc được, có số nếu có. */
    reason: z.string().min(1).max(300),
});
/** Một chỗ thiếu người: cần thêm ai, bao lâu. */
export const StaffingGap = z.object({
    /** Kỹ năng cần, lấy từ danh mục tech stack của tổ chức khi có. */
    skill: z.string().min(1).max(100),
    people: z.number().int().min(1).max(20),
    days: z.number().int().min(1).max(365),
});
/**
 * Không xếp nổi thì phải nói **cần thêm gì**.
 *
 * Hai lối thoát, và PM chọn: thêm người, hoặc giãn hạn. Chỉ đưa ra một lối là
 * ép một quyết định thuộc về người quản lý — thêm người có khi không có ngân
 * sách, giãn hạn có khi vướng hợp đồng, và hệ thống không biết điều nào đúng.
 */
export const RescheduleShortfall = z.object({
    needPeople: z.array(StaffingGap).max(5),
    /** Số ngày phải giãn hạn nếu không thêm người. `0` = giãn hạn không cứu được. */
    extendDays: z.number().int().min(0).max(365),
    explanation: z.string().min(1).max(1_000),
});
export const ReschedulePlanOutput = z.object({
    /**
     * Rỗng là hợp lệ — khi đó `shortfall` phải có. Xem ghi chú ở đầu file.
     */
    moves: z.array(RescheduleMove).max(20),
    shortfall: RescheduleShortfall.nullable(),
    summary: z.string().max(1_000),
});
/** Vì sao một nước đi bị chặn. Mã chứ không phải câu chữ — giao diện tự dịch. */
export const RescheduleViolation = z.enum([
    /** Mã task không có trong dự án này. */
    'unknown_task',
    /** Người nhận không ở trong dự án. */
    'not_a_member',
    /** Người nhận đã qua ngày cuối làm việc trước khi việc kết thúc. */
    'assignee_leaving',
    /** Giờ khả dụng thật của người nhận không đủ cho ước lượng của việc. */
    'capacity_exceeded',
    /** Hạn chót trước ngày bắt đầu, hoặc thiếu một trong hai. */
    'invalid_dates',
    /** Lịch mới vi phạm phụ thuộc: bắt đầu trước khi việc đi trước xong. */
    'dependency_violation',
]);
export const RescheduleCheckItem = z.object({
    taskKey: z.string(),
    ok: z.boolean(),
    violations: z.array(RescheduleViolation),
    /** Con số của lần kiểm: giờ cần, giờ có, ngày vi phạm phụ thuộc. */
    metrics: z.record(z.string(), z.number()),
    /**
     * Điều bộ kiểm **không kết luận được** — APM-H-n. Không phải vi phạm:
     * - `no_own_schedule`: người nhận chưa khai lịch riêng, năng lực tính theo lịch mặc định.
     * - `no_dates`: việc không có ngày bắt đầu/hạn (trước và sau nước đi) ⇒ không có khoảng để đếm giờ.
     */
    unverified: z.array(z.enum(['no_own_schedule', 'no_dates'])).optional(),
});
export const RescheduleCheckResult = z.object({
    ok: z.boolean(),
    items: z.array(RescheduleCheckItem),
});
/**
 * Duyệt **một phần** phương án — ADR-005, cùng hình dạng với duyệt gợi ý rủi ro.
 *
 * Duyệt trọn gói là cách nhanh nhất để một phương án tám nước đi được bấm qua
 * mà không ai đọc nước thứ sáu.
 */
export const ApplyRescheduleInput = z.object({
    indexes: z.array(z.number().int().min(0).max(19)).min(1).max(20),
});
//# sourceMappingURL=reschedule-plan.js.map